import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileUp, Loader2, Paperclip, Search, Ship, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { EmailReportButton } from "../components/EmailReportButton";
import { useAuth } from "../context/AuthContext";
import { ExpedientePicker } from "../components/OsintLinks";

const LISTS = ["OFAC", "FCDO", "UANI", "EU", "ASO", "MFAT", "GAC", "SECO", "UN"];
const AUTH = { OFAC: "EE.UU. — OFAC", FCDO: "Reino Unido — FCDO", UANI: "United Against Nuclear Iran", EU: "Unión Europea", ASO: "Australia — ASO", MFAT: "Nueva Zelanda — MFAT", GAC: "Canadá — GAC", SECO: "Suiza — SECO", UN: "Naciones Unidas" };

function VesselDetail({ v, onClose, t }) {
  const [expId, setExpId] = useState("");
  const [attaching, setAttaching] = useState(false);
  const attach = async () => {
    setAttaching(true);
    try { await api.post(`/vessels/${v.id}/attach`, { expediente_id: expId }); toast.success(t("osint.attached")); } catch (e) { toast.error(apiError(e)); } finally { setAttaching(false); }
  };
  const rows = [[t("vs.name"), v.name], ["IMO", v.imo], [t("vs.type"), v.vessel_type], [t("vs.flag"), v.flag], [t("vs.built"), v.built],
    [t("vs.sanction_date"), v.ofac_date], [t("vs.first_date"), v.first_date], [t("vs.last_date_full"), v.last_date], [t("vs.program"), v.programs]];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="vessel-detail-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><Ship className="w-5 h-5 text-[#044CBC]" /> {v.name}</h3>
            <div className="text-xs text-slate-500">IMO {v.imo || "—"} · {v.flag || "—"} · {v.vessel_type || "—"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="vessel-detail-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="vessel-detail-fields">
          {rows.map(([k, val]) => <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-800 text-right">{val || "—"}</span></div>)}
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{t("vs.by_authority")}</div>
          <div className="grid sm:grid-cols-3 gap-2" data-testid="vessel-detail-lists">
            {LISTS.map((l) => { const d = v.lists?.[l]; return (
              <div key={l} className={`rounded-lg border px-3 py-2 ${d ? "border-orange-200 bg-orange-50" : "border-slate-100 bg-slate-50 text-slate-400"}`}>
                <div className="text-xs font-bold">{l} <span className="font-normal">· {AUTH[l]}</span></div>
                <div className="text-sm font-semibold">{d || t("vs.not_listed")}</div>
              </div>); })}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <ExpedientePicker value={expId} onChange={setExpId} testid="vessel-exp-select" />
          <button onClick={attach} disabled={!expId || attaching} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#16335F] text-[#16335F] text-sm font-bold hover:bg-[#EFF4FC] disabled:opacity-50" data-testid="vessel-detail-attach">{attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {t("osint.attach")}</button>
          <button onClick={() => downloadFile(`/vessels/${v.id}/excel`, `ofac_vessel_${v.imo || v.id}.xlsx`)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 text-emerald-800 bg-emerald-50 text-sm font-bold" data-testid="vessel-detail-xlsx"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <EmailReportButton pdfPath={`/vessels/${v.id}/pdf`} filename={`ofac_vessel_${v.imo || v.id}.pdf`} subject={`Ficha OFAC Vessel — ${v.name}`} module="OFAC Vessel" summary={`${v.name} — IMO ${v.imo || "—"}`} testid="vessel-detail-email" className="!text-sm !px-4" />
          <button onClick={() => downloadFile(`/vessels/${v.id}/pdf`, `ofac_vessel_${v.imo || v.id}.pdf`)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#033A8C] text-white text-sm font-bold" data-testid="vessel-detail-pdf"><Download className="w-4 h-4" /> {t("common.download_pdf")}</button>
        </div>
      </div>
    </div>
  );
}

const EMPTY = { q: "", flag: "", vtype: "", imo: "", lst: "" };
const qs = (f) => Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");

function ImportBox({ meta, onDone, t }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!window.confirm(t("vs.import_confirm"))) return;
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.onerror = rej; rd.readAsDataURL(f); });
      const { data } = await api.post("/vessels/import", { file_base64: b64, filename: f.name });
      toast.success(`${data.inserted} ${t("vs.imported")}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} ${t("vs.import_warn")}`);
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ec-card p-4 flex flex-wrap items-center gap-4" data-testid="vessel-import-box">
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-bold text-slate-800">{t("vs.import_title")}</div>
        <div className="text-xs text-slate-500">{t("vs.import_help")}</div>
        {meta?.last_import && (
          <div className="text-xs text-slate-600 mt-1" data-testid="vessel-last-import">
            {t("vs.last_import")}: <b>{fmtDate(meta.last_import.last_import)}</b> · {meta.last_import.count} {t("vs.records")} · {meta.last_import.by} · {meta.last_import.filename}
          </div>
        )}
      </div>
      <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="vessel-import-label">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />} {t("vs.import_btn")}
        <input type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="vessel-import-input" />
      </label>
    </div>
  );
}

export default function OfacVessel() {
  const { t } = useLang();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const loadMeta = () => api.get("/vessels/meta").then((r) => setMeta(r.data)).catch(() => {});
  useEffect(() => { loadMeta(); }, []);

  const search = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get(`/vessels?${qs(f)}`);
      setRes(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="ofac-vessel-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Ship className="w-7 h-7 text-[#044CBC]" /> {t("vs.title")}</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">{t("vs.subtitle")}</p>
      </div>
      {(user?.role === "superadmin" || user?.is_super || user?.real_role === "superadmin") && <ImportBox meta={meta} onDone={() => { loadMeta(); setRes(null); }} t={t} />}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="ec-card px-4 py-2.5" data-testid="vessel-total-card"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("vs.total")}</div><div className="font-heading text-xl font-extrabold text-slate-900">{(meta?.total || 0).toLocaleString()}</div></div>
        <div className="ec-card px-4 py-2.5"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("vs.flags")}</div><div className="font-heading text-xl font-extrabold text-slate-900">{meta?.flags?.length || 0}</div></div>
        <div className="ec-card px-4 py-2.5"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("vs.types")}</div><div className="font-heading text-xl font-extrabold text-slate-900">{meta?.types?.length || 0}</div></div>
        <div className="ec-card px-4 py-2.5"><div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("vs.last_import")}</div><div className="font-heading text-sm font-extrabold text-slate-900 mt-1">{meta?.last_import ? fmtDate(meta.last_import.last_import) : "—"}</div></div>
      </div>
      <form onSubmit={search} className="ec-card p-6 space-y-4" data-testid="vessel-search-form">
        <div className="grid md:grid-cols-5 gap-4">
          <div className="md:col-span-2"><label className="ec-label">{t("vs.name")}</label><input className="ec-input" value={f.q} onChange={set("q")} placeholder={t("vs.name_ph")} data-testid="vessel-q" /></div>
          <div><label className="ec-label">IMO</label><input className="ec-input" value={f.imo} onChange={set("imo")} placeholder="9422964" data-testid="vessel-imo" /></div>
          <div><label className="ec-label">{t("vs.flag")}</label>
            <select className="ec-input" value={f.flag} onChange={set("flag")} data-testid="vessel-flag"><option value="">{t("common.all")}</option>{(meta?.flags || []).map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
          <div><label className="ec-label">{t("vs.type")}</label>
            <select className="ec-input" value={f.vtype} onChange={set("vtype")} data-testid="vessel-type"><option value="">{t("common.all")}</option>{(meta?.types || []).map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select className="ec-input w-auto" value={f.lst} onChange={set("lst")} data-testid="vessel-list"><option value="">{t("vs.any_list")}</option>{(meta?.lists || []).map((x) => <option key={x} value={x}>{x}</option>)}</select>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60" data-testid="vessel-search-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {t("common.search")}
          </button>
          <button type="button" onClick={() => { setF(EMPTY); setRes(null); }} className="px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-bold text-slate-700" data-testid="vessel-clear-btn">{t("common.clear")}</button>
          {res && res.items.length > 0 && (
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={() => downloadFile(`/vessels/export/excel?${qs(f)}`, "ofac_vessel.xlsx")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-emerald-300 text-emerald-800 bg-emerald-50 text-sm font-bold" data-testid="vessel-xlsx-btn">
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </button>
              <EmailReportButton pdfPath={`/vessels/export/pdf?${qs(f)}`} filename="ofac_vessel.pdf" subject="Resultados OFAC Vessel" module="OFAC Vessel" summary={Object.values(f).filter(Boolean).join(" · ") || "Lista completa"} testid="vessel-export-email" className="!text-sm !px-4" />
              <button type="button" onClick={() => downloadFile(`/vessels/export/pdf?${qs(f)}`, "ofac_vessel.pdf")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#033A8C] text-white text-sm font-bold" data-testid="vessel-pdf-btn">
                <Download className="w-4 h-4" /> {t("common.download_pdf")}
              </button>
            </div>
          )}
        </div>
      </form>
      {sel && <VesselDetail v={sel} onClose={() => setSel(null)} t={t} />}
      {res && (
        <div className="ec-card overflow-hidden" data-testid="vessel-results">
          <div className="px-4 py-2.5 text-xs font-bold text-slate-600 border-b border-slate-100" data-testid="vessel-count">{res.total.toLocaleString()} {t("vs.results")}{res.total > res.items.length ? ` · ${t("vs.showing")} ${res.items.length}` : ""} <span className="font-normal text-slate-400">· {t("vs.click_hint")}</span></div>
          {res.items.length === 0 ? <div className="p-6 text-sm text-slate-500" data-testid="vessel-empty">{t("vs.none")}</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead><tr className="bg-[#16335F] text-white text-left">
                  {[t("vs.name"), t("vs.type"), t("vs.flag"), "IMO", t("vs.sanction_date"), t("vs.program")].map((h) => <th key={h} className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{h}</th>)}
                </tr></thead>
                <tbody>
                  {res.items.map((v) => (
                    <tr key={v.id} onClick={() => setSel(v)} className="border-t border-slate-100 hover:bg-sky-50 cursor-pointer" data-testid={`vessel-row-${v.id}`}>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{v.name}{v.built ? <div className="text-xs font-normal text-slate-500">{t("vs.built")}: {v.built}</div> : null}</td>
                      <td className="px-3 py-2.5 text-slate-600">{v.vessel_type || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{v.flag || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{v.imo || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{v.ofac_date || v.first_date || "—"}{v.last_date && v.last_date !== (v.ofac_date || v.first_date) ? <div className="text-xs text-slate-400">{t("vs.last_date")}: {v.last_date}</div> : null}</td>
                      <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1">{(v.list_names || []).map((l) => <span key={l} title={v.lists?.[l]} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${l === "OFAC" ? "bg-orange-100 text-[#033A8C]" : "bg-slate-100 text-slate-700"}`}>{l}</span>)}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
